import { IconButton, styled } from '@mui/material';
import { forwardRef } from 'react';
import Icon from 'shell/components/Icon';
import Tooltip from 'shell/components/Tooltip';
import { getTooltipProps } from 'utils/helper';

const SIZES = ['undersized', 'xsmall', 'small', 'medium', 'large'];
const VARIANTS = ['circle', 'square'];

const TooltipAnchor = styled('span')({
    display: 'inline-flex',
});

const CustomIconButton = styled(IconButton)(({ disabled, $size, variant, theme }) => {
    const getSize = (variant) => {
        return variant === 'circle'
            ? {
                  undersized: '1rem',
                  xsmall: '1.5rem',
                  small: '1.75rem',
                  medium: '2.25rem',
                  large: '2.75rem',
              }[$size]
            : {
                  small: '1.5rem',
                  medium: '2.25rem',
                  large: '2.5rem',
              }[$size];
    };

    const styles = {
        circle: {
            background: theme.palette.ensightful.primary[500],
            color: theme.palette.ensightful.shades[0],
            '&:hover': {
                background: theme.palette.ensightful.primary[700],
            },
            '&:active': {
                background: theme.palette.ensightful.primary[800],
            },
            ...(disabled && {
                '&.Mui-disabled': {
                    background: theme.palette.ensightful.neutral[200],
                    color: theme.palette.ensightful.neutral[500],
                    pointerEvents: 'none',
                    '&:active': {
                        background: theme.palette.ensightful.neutral[200],
                    },
                },
            }),
        },
        square: {
            borderRadius: '4px',
            background: 'transparent',
            color: theme.palette.ensightful.neutral[700],
            '&:hover': {
                background: theme.palette.ensightful.neutral[100],
                color: theme.palette.ensightful.neutral[900],
            },
            '&:active': {
                background: theme.palette.ensightful.neutral[200],
                color: theme.palette.ensightful.neutral[900],
            },
            ...(disabled && {
                '&.Mui-disabled': {
                    color: theme.palette.ensightful.neutral[400],
                    pointerEvents: 'none',
                    '&:hover': {
                        background: 'transparent',
                    },
                    '&:active': {
                        background: 'transparent',
                    },
                },
            }),
        },
    }[variant];

    return {
        width: getSize(variant),
        height: getSize(variant),
        transition: theme.transitions.default,
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
        ...styles,
    };
});

const CustomIcon = styled(Icon)(({ size }) => {
    const fontSize = {
        undersized: '0.6rem',
        xsmall: '1rem',
        small: '1rem',
        medium: '1.5rem',
        large: '1.5rem',
    }[size];

    return {
        fontSize,
    };
});

/**
 * A customizable icon button component that supports different sizes, variants, and tooltips.
 * It renders an icon inside a button with optional tooltip functionality.
 *
 * @param {boolean} [disabled=false] - Whether the button is disabled.
 * @param {string} kind - The kind of icon to display. Must be one of the keys in the icons object defined in Icon.js (e.g., 'add', 'arrowDown', 'checkmark', 'delete', 'edit', 'search', etc.).
 * @param {string} size - The size of the button. For 'circle' variant: 'undersized' (1rem), 'xsmall' (1.5rem), 'small' (1.75rem), 'medium' (2.25rem), 'large' (2.75rem). For 'square' variant: 'small' (1.5rem), 'medium' (2.25rem), 'large' (2.5rem). Note: 'xsmall' and 'undersized' are only valid for 'circle' variant.
 * @param {object} [tooltip] - Tooltip configuration object.
 * @param {string|React.ReactElement} tooltip.component - The tooltip content.
 * @param {string} [tooltip.maxWidth='8.75rem'] - The maximum width of the tooltip.
 * @param {string} [tooltip.placement] - The placement of the tooltip.
 * @param {string} variant - The variant of the button. Valid variants are 'circle' and 'square'.
 * @param {function} [onClick] - The click handler function.
 * @param {object} [rest] - Additional props passed to the underlying IconButton component.
 * @param {React.Ref} ref - Ref forwarded to the IconButton component.
 * @returns {React.ReactElement} The rendered icon button component.
 */
export default forwardRef(({ disabled, kind, size, tooltip, variant, onClick, ...rest }, ref) => {
    const { className, style, ...buttonProps } = rest;

    if (tooltip && !tooltip.component) {
        throw new Error(
            `Shell: Invalid tooltip in component IconButton. Expected "tooltip.component" to be "string" or "ReactElement", but got "${typeof tooltip.component}" instead.`
        );
    }
    if (!SIZES.includes(size)) throw new Error(`Shell: Invalid size "${size}" in component IconButton.`);
    if (!VARIANTS.includes(variant)) throw new Error(`Shell: Invalid variant "${variant}" in component IconButton.`);
    if ((size === 'xsmall' || size === 'undersized') && variant === 'square') {
        throw new Error(
            `Shell: Invalid size "${size}" for variant "${variant}" in component IconButton. Please use one of "small", "medium" or "large" for size.`
        );
    }

    const IconButtonComponent = (
        <CustomIconButton
            ref={ref}
            className={!tooltip ? className : undefined}
            disabled={disabled}
            $size={size}
            variant={variant}
            onClick={disabled ? undefined : onClick}
            style={!tooltip ? style : undefined}
            {...buttonProps}
        >
            <CustomIcon kind={kind} size={size} />
        </CustomIconButton>
    );

    return tooltip ? (
        <Tooltip {...getTooltipProps(tooltip)}>
            <TooltipAnchor className={className} style={style}>
                {IconButtonComponent}
            </TooltipAnchor>
        </Tooltip>
    ) : (
        IconButtonComponent
    );
});
